// =====================================================
// Chat Store - Global State Management
// =====================================================

import { create } from 'zustand';
import { Message, ChatState } from '../types';

// Generate or retrieve persistent user ID from localStorage
const USER_ID_KEY = 'bh_chatbot_user_id';

const getOrCreateUserId = (): string => {
  let userId = localStorage.getItem(USER_ID_KEY);
  if (!userId) {
    // Generate a unique user ID (persists across sessions)
    userId = `user_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    localStorage.setItem(USER_ID_KEY, userId);
  }
  return userId;
};

interface ChatStore extends ChatState {
  // User identification
  userId: string;              // Persistent user ID (from localStorage)

  // Actions
  setIsOpen: (isOpen: boolean) => void;
  addMessage: (message: Message) => void;
  setSessionId: (sessionId: string) => void;
  setLanguage: (language: 'en' | 'es') => void;
  setIsLoading: (isLoading: boolean) => void;
  setCurrentPage: (page: string) => void;
  clearMessages: () => void;
  // Track submitted tour requests
  submittedHomeIds: Set<number>;
  submittedFloorPlanIds: Set<number>;
  markHomeSubmitted: (homeId: number) => void;
  markFloorPlanSubmitted: (floorPlanId: number) => void;
  isHomeSubmitted: (homeId: number) => boolean;
  isFloorPlanSubmitted: (floorPlanId: number) => boolean;
  // Photo enlargement modal state
  photoModalOpen: boolean;
  photoModalImages: string[];
  photoModalIndex: number;
  photoModalTitle: string;
  photoModalOnClose: ((finalIndex: number) => void) | null;
  openPhotoModal: (images: string[], startIndex: number, onClose?: (finalIndex: number) => void, title?: string) => void;
  closePhotoModal: () => void;
  setPhotoModalIndex: (index: number) => void;
  // Virtual tour modal state
  virtualTourModalOpen: boolean;
  virtualTourModalUrl: string;
  virtualTourModalTitle: string;
  openVirtualTourModal: (url: string, title?: string) => void;
  closeVirtualTourModal: () => void;
}

export const useChatStore = create<ChatStore>((set, get) => ({
  // Initial state
  isOpen: false,
  messages: [],
  sessionId: null,
  userId: getOrCreateUserId(),  // Persistent user ID from localStorage
  language: 'en',
  isLoading: false,
  currentPage: window.location.href,
  submittedHomeIds: new Set(),
  submittedFloorPlanIds: new Set(),

  // Actions
  setIsOpen: (isOpen) => set({ isOpen }),

  addMessage: (message) => set((state) => ({
    messages: [...state.messages, message]
  })),

  setSessionId: (sessionId) => set({ sessionId }),

  setLanguage: (language) => set({ language }),

  setIsLoading: (isLoading) => set({ isLoading }),

  setCurrentPage: (page) => set({ currentPage: page }),

  clearMessages: () => set({
    messages: [],
    sessionId: null
  }),

  // Track submitted tour requests
  markHomeSubmitted: (homeId) => set((state) => ({
    submittedHomeIds: new Set([...state.submittedHomeIds, homeId])
  })),

  markFloorPlanSubmitted: (floorPlanId) => set((state) => ({
    submittedFloorPlanIds: new Set([...state.submittedFloorPlanIds, floorPlanId])
  })),

  isHomeSubmitted: (homeId) => get().submittedHomeIds.has(homeId),

  isFloorPlanSubmitted: (floorPlanId) => get().submittedFloorPlanIds.has(floorPlanId),

  // Photo enlargement modal state
  photoModalOpen: false,
  photoModalImages: [],
  photoModalIndex: 0,
  photoModalTitle: '',
  photoModalOnClose: null,

  openPhotoModal: (images, startIndex, onClose, title) => set({
    photoModalOpen: true,
    photoModalImages: images,
    photoModalIndex: startIndex,
    photoModalTitle: title || '',
    photoModalOnClose: onClose || null
  }),

  closePhotoModal: () => {
    const { photoModalOnClose, photoModalIndex } = get();
    if (photoModalOnClose) {
      photoModalOnClose(photoModalIndex);
    }
    set({
      photoModalOpen: false,
      photoModalImages: [],
      photoModalIndex: 0,
      photoModalTitle: '',
      photoModalOnClose: null
    });
  },

  setPhotoModalIndex: (index) => set({ photoModalIndex: index }),

  // Virtual tour modal state
  virtualTourModalOpen: false,
  virtualTourModalUrl: '',
  virtualTourModalTitle: '',

  openVirtualTourModal: (url, title) => set({
    virtualTourModalOpen: true,
    virtualTourModalUrl: url,
    virtualTourModalTitle: title || ''
  }),

  closeVirtualTourModal: () => set({
    virtualTourModalOpen: false,
    virtualTourModalUrl: '',
    virtualTourModalTitle: ''
  })
}));